import { connectRouter, RouterState, routerMiddleware } from 'connected-react-router'
import { History } from 'history'
import LogRocket from 'logrocket'
import { createStore, applyMiddleware, combineReducers, compose, Store } from 'redux'
import createSagaMiddleware from 'redux-saga'
import { Persistor, persistReducer, persistStore } from 'redux-persist'
import { createWhitelistFilter } from 'redux-persist-transform-filter'
import storage from 'redux-persist/lib/storage'

import {
    campaignsReducer,
    CampaignState,
    CampaignsState,
    getList,
    CampaignViews,
    getSubmissionsSummary,
    SubmissionsSummaryViews,
    selectDetails,
    selectSummary,
    selectProgress,
    selectPerformance,
} from './campaigns'
import { profileReducer, ProfileState, getDetails } from './profile'
import { constantsReducer, ConstantsState } from './constants'
import { brandsReducer, BrandsState, getList as getBList, selectBrandsById, selectBrandDetail } from './brands'
import {
    submissionsReducer,
    SubmissionsState,
    selectList as selectSList,
    PartiposterProfile,
    SUBMISSION_STATUS_ENUM,
    selectMassPayoutProgress,
    selectMassPayoutSuccess,
    selectMassPayoutFailed,
    SocialMediaAccount,
} from './submissions'
import { selectTaskIds, tasksReducer, TasksState, selectTasks } from './tasks'
import { selectList as selectRList, selectPriorityList as selectPList } from './registrations'
import { filterReducer, FilterState } from './filter'
import { confirmPopupReducer, ConfirmPopupState } from './confirmPopup'
import {
    teamsReducer,
    TeamsState,
    selectTeams as selectTList,
    selectTeamMembers,
    selectTeamDetail,
    selectTeamBrands,
} from './teams'
import {
    requestsReducer,
    RequestsState,
    createGetLoading,
    selectIsLoading,
    createGetErrors,
    createGetPagination,
    StateKey,
    PaginationStateKey,
} from './requests'
import { createGetKey, selectConstants } from './constants'
import sagas from '../sagas'
import { partiposterReducer, PartiposterState, selectPartiposterProfiles } from './partiposter'
import { reviewReducer, ReviewState, selectSelectedCampaign } from './review'
import { registrationsReducer, RegistrationsState, selectRegistrationForm } from './registrations'
import { bannerManagerReducer, BannerManagerState } from './banners'
import { commentReducer, CampaignCommentState } from './comments'
import { campaignTemplatesReducer, CampaignTemplatesState, selectTemplateData } from './campaignTemplates'
import { appPreviewReducer, AppPreviewState } from './appPreview'
import { createSelector } from 'reselect'
import { errorsReducer, ErrorsState } from 'state/error'
import _ from 'lodash'
import { PLATFORM_ENUM } from 'services/api/constants'
import { campaignReportReducer, CampaignReportState } from './campaignReport'
import { ambassadorReducer, AmbassadorState } from './ambassadors'

export interface BaseAction {
    type: string
    payload?: any
}

export interface State {
    router: RouterState
    requests: RequestsState
    profile: ProfileState
    filter: FilterState
    constants: ConstantsState
    brands: BrandsState
    campaigns: CampaignsState
    submissions: SubmissionsState
    tasks: TasksState
    teams: TeamsState
    partiposter: PartiposterState
    review: ReviewState
    registrations: RegistrationsState
    banner: BannerManagerState
    confirmPopup: ConfirmPopupState
    errors: ErrorsState
    comment: CampaignCommentState
    campaignTemplates: CampaignTemplatesState
    appPreview: AppPreviewState
    campaignReport: CampaignReportState
    ambassadors: AmbassadorState
}

export interface PerformanceState {
    uuid: string
    partiposter_profile: PartiposterProfile
    totalEarnings: number
    total_earnings_without_adjustment?: number
    payout_remark?: string
    earnings_adjustment_custom_reason?: string
    earnings_adjustment_reason: string
    earnings_adjustment_source?: string
    is_paid_outside_app: boolean | null
    paid_outside_app_custom_payment_method?: string
    paid_outside_app_custom_reason?: string
    paid_outside_app_payment_method?: string
    paid_outside_app_reason?: string
    paymentStatus: SUBMISSION_STATUS_ENUM
    taskPerformances: { [taskId: string]: { [performanceKey: string]: { count: string } } }
    instagram_follower_count?: number | null
    tiktok_follower_count?: number | null
    twitter_follower_count?: number | null
    youtube_follower_count?: number | null
    facebook_follower_count?: number | null
    xiaohongshu_follower_count?: number | null
}

interface ConfigureStore {
    store: Store<State>
    persistor: Persistor
}

export const configureStore = (initialState: State, history: History): ConfigureStore => {
    const sagaMiddleware = createSagaMiddleware()
    const persistConfig = {
        key: 'root',
        storage: storage,
        whitelist: ['filter', 'review'], // Only persist filter
        transforms: [createWhitelistFilter('review', ['filterModel', 'filterOptions', 'sortItem'])],
    }

    const rootReducers = combineReducers<State>({
        router: connectRouter(history),
        profile: profileReducer,
        filter: filterReducer,
        constants: constantsReducer,
        brands: brandsReducer,
        requests: requestsReducer,
        campaigns: campaignsReducer,
        submissions: submissionsReducer,
        tasks: tasksReducer,
        teams: teamsReducer,
        partiposter: partiposterReducer,
        review: reviewReducer,
        registrations: registrationsReducer,
        banner: bannerManagerReducer,
        confirmPopup: confirmPopupReducer,
        errors: errorsReducer,
        comment: commentReducer,
        campaignTemplates: campaignTemplatesReducer,
        appPreview: appPreviewReducer,
        campaignReport: campaignReportReducer,
        ambassadors: ambassadorReducer,
    })

    const reducers = persistReducer(persistConfig, rootReducers)

    const composeEnhancers = (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose
    const middlewares = applyMiddleware(sagaMiddleware, routerMiddleware(history), LogRocket.reduxMiddleware())
    const store = createStore(reducers, initialState, composeEnhancers(middlewares))
    sagaMiddleware.run(sagas)
    return { store, persistor: persistStore(store) }
}

// Selectors

const selectRequests = (state: State) => state.requests
export const selectProfile = (state: State) => state.profile
const selectBrands = (state: State) => state.brands
export const selectSubmissions = (state: State) => state.submissions
export const selectRegistrations = (state: State) => state.registrations
export const selectCampaigns = (state: State) => state.campaigns
const selectTeams = (state: State) => state.teams
export const selectPartiposter = (state: State) => state.partiposter

export const createGetRequestsAreLoading = (stateKeys: StateKey[]) => (state: State) =>
    createGetLoading(stateKeys)(selectRequests(state))

export const selectRequestsAreLoading = (stateKeys: StateKey[]) => (state: State) =>
    selectIsLoading(selectRequests(state), stateKeys)

export const createGetRequestPagination = (stateKey: PaginationStateKey) => (state: State) =>
    createGetPagination(stateKey)(selectRequests(state))

export const createGetRequestErrors = (stateKey: StateKey[]) => (state: State) =>
    createGetErrors(stateKey)(selectRequests(state))

export const selectProfileDetails = (state: State) => getDetails(selectProfile(state))

export const createGetConstantsByKey = (constantsKey: string) => (state: State) =>
    createGetKey(constantsKey)(selectConstants(state))

export const selectCampaign = (campaignUuid: string) => (state: State) =>
    selectDetails(selectCampaigns(state), campaignUuid)

export const selectCampaignSummary = (campaignUuid: string) => (state: State) =>
    selectSummary(selectCampaigns(state), campaignUuid)

export const selectCampaignSubmissionsSummary = (campaignUuid: string) => (state: State) =>
    selectCampaigns(state).campaignSubmissionsSummary[campaignUuid]

export const selectSubmissionPayoutProgress = (campaignUuid: string) => (state: State) =>
    selectMassPayoutProgress(state, campaignUuid)

export const selectSubmissionMassPayoutSuccess = (campaignUuid: string) => (state: State) =>
    selectMassPayoutSuccess(state, campaignUuid)

export const selectSubmissionMassPayoutFailed = (campaignUuid: string) => (state: State) =>
    selectMassPayoutFailed(state, campaignUuid)

export const selectCampaignProgress = (state: State) => selectProgress(selectCampaigns(state))
export const selectCampaignPerformance = (state: State) => selectPerformance(selectCampaigns(state))
export const makeSelectCampaignPerformanceData = () =>
    createSelector(
        selectCampaignPerformance,
        selectTaskIds,
        selectPartiposterProfiles,
        (campaignPerformances, campaignTaskIds, profiles): PerformanceState[] => {
            return campaignPerformances.map(campaignPerformance => {
                const {
                    uuid,
                    submission_tasks: tasks,
                    total_earnings: totalEarnings,
                    total_earnings_without_adjustment,
                    payout_remark,
                    earnings_adjustment_reason,
                    earnings_adjustment_custom_reason,
                    earnings_adjustment_source,
                    status: paymentStatus,
                    instagram_follower_count,
                    tiktok_follower_count,
                    twitter_follower_count,
                    youtube_follower_count,
                    facebook_follower_count,
                    xiaohongshu_follower_count,
                    is_paid_outside_app,
                    paid_outside_app_custom_payment_method,
                    paid_outside_app_custom_reason,
                    paid_outside_app_payment_method,
                    paid_outside_app_reason,
                } = campaignPerformance

                const tasksById = _.keyBy(tasks, 'task_uuid')

                const taskPerformances = campaignTaskIds.reduce<PerformanceState['taskPerformances']>(
                    (memo, taskId: string) => {
                        const performance = tasksById[taskId] ? tasksById[taskId].performance : {}
                        const { extras, ...standardPerformance } = performance

                        memo[taskId] = {
                            ...standardPerformance,
                            ...extras,
                        }
                        return memo
                    },
                    {},
                )

                return {
                    uuid,
                    partiposter_profile: profiles[campaignPerformance.partiposterId],
                    totalEarnings,
                    total_earnings_without_adjustment,
                    earnings_adjustment_reason,
                    earnings_adjustment_custom_reason,
                    earnings_adjustment_source,
                    is_paid_outside_app,
                    paid_outside_app_custom_payment_method,
                    paid_outside_app_custom_reason,
                    paid_outside_app_payment_method,
                    paid_outside_app_reason,
                    payout_remark,
                    paymentStatus,
                    taskPerformances,
                    instagram_follower_count,
                    tiktok_follower_count,
                    twitter_follower_count,
                    youtube_follower_count,
                    facebook_follower_count,
                    xiaohongshu_follower_count,
                }
            })
        },
    )

export const createGetCampaignsList = (state: State) => (viewType: CampaignViews) =>
    getList(selectCampaigns(state), viewType)

export const createGetSubmissionsSummary = (state: State) => (viewType: SubmissionsSummaryViews) =>
    getSubmissionsSummary(selectCampaigns(state), viewType)

export const getBrandsList = (state: State) => getBList(selectBrands(state))

export const selectSubmissionsList = (state: State) => selectSList(selectSubmissions(state))

export const selectRegistrationsList = (state: State) => selectRList(selectRegistrations(state))

export const selectPriorityList = (state: State) => selectPList(selectRegistrations(state))

export const selectPriorityListRows = createSelector(
    [selectSelectedCampaign, selectTasks, selectPartiposterProfiles, selectPriorityList, selectRegistrationForm],
    (campaign, tasks, profiles, priorityList, registrationForm) => {
        const campaignUuid = campaign?.uuid
        const registrationFormBlocksById =
            registrationForm && registrationForm.blocks ? _.keyBy(registrationForm.blocks, 'uuid') : {}
        return priorityList.map((registration, index: number) => {
            const profile = profiles[registration.partiposterId]
            const { social_media_accounts: socialMediaAccounts } = profile
            const igProfile = socialMediaAccounts.filter(
                (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.IG,
            )[0]
            const fbProfile = socialMediaAccounts.filter(
                (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.FB,
            )[0]
            const tiktokProfile = socialMediaAccounts.filter(
                (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.TIKTOK,
            )[0]
            const twitterProfile = socialMediaAccounts.filter(
                (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.TWITTER,
            )[0]
            const xiaohongshuProfile = socialMediaAccounts.filter(
                (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.XIAOHONGSHU,
            )[0]
            const hasFbTask = tasks.find(task => ['facebook_post'].includes(task.type))
            const { unmet_requirement_types } = registration
            const hasRequirements = (campaign && campaign.requirements.length > 0) || false

            return {
                id: index + 1,
                uuid: registration.uuid,
                profile: {
                    unmetRequirement: unmet_requirement_types,
                    profile,
                    hasRequirements,
                    recommended: registration.recommended,
                },
                socialMediaInfo: {
                    profiles: [igProfile, fbProfile, tiktokProfile, twitterProfile, xiaohongshuProfile],
                },
                action: {
                    registration: registration,
                    registrationFormBlocksById: registrationFormBlocksById,
                    campaignUuid: campaignUuid ? campaignUuid : '',
                    profile: profile,
                    igUserName: igProfile && igProfile.username ? igProfile.username : undefined,
                    hasFbTask: hasFbTask ? true : false,
                    fbUserName: fbProfile && fbProfile.username ? fbProfile.username : undefined,
                },
            }
        })
    },
)

export const selectTeamsList = (state: State) => selectTList(selectTeams(state))

export const selectTeamMembersList = (teamUuid: string) => (state: State) =>
    selectTeamMembers(selectTeams(state), teamUuid)

export const selectTeamBrandsList = (teamUuid: string) => (state: State) =>
    selectTeamBrands(selectTeams(state), selectBrandsById(selectBrands(state)), teamUuid)

export const selectTeam = (teamUuid: string) => (state: State) => selectTeamDetail(selectTeams(state), teamUuid)

export const selectBrand = (brandUuid: string) => (state: State) => selectBrandDetail(selectBrands(state), brandUuid)

export const selectCampaignTemplates = (state: State) => state.campaignTemplates

export const selectAppPreview = (state: State) => state.appPreview

export const selectCampaignByTemplateData = createSelector(
    [selectTemplateData, selectCampaigns],
    (templateData, campaigns): null | CampaignState => {
        if (!templateData || !templateData.campaign || !templateData.campaign.uuid) {
            return null
        }
        const campaignUuid = templateData.campaign.uuid
        return selectDetails(campaigns, campaignUuid)
    },
)
